<template>
  <v-layout
    row
    wrap
  >
    <v-flex xs12>
      <v-card class="max-shadow">
        <v-toolbar
          color="white"
          flat
        >
          <v-toolbar-title class="blue-grey--text text--darken-3">
            Branding
          </v-toolbar-title>
        </v-toolbar>
        <v-toolbar
          color="white"
          flat
        >
          <v-toolbar-title class="blue-grey--text text--darken-3">
            Logo
          </v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-btn
              color="primary"
              flat
              @click="$refs.logoInput.click();"
            >
              <input
                id="logoInput"
                ref="logoInput"
                accept="image/*"
                type="file"
                @change="changeLogo"
              >Change
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <p class="subheading px-3 pt-3">
          This company logo will be used for E-mails, badges and app welcome screen
        </p>
        <v-layout
          align-center
          class="pa-3"
        >
          <template v-if="account.logo">
            <v-card
              height="200"
              width="300"
              flat
            >
              <v-layout
                justify-center
                align-center
                fill-height
                row
              >
                <v-flex xs12>
                  <v-img
                    :src="account.logo.download_url"
                    refs="imageElement"
                    contain
                    height="100"
                    @load="getSwatch($event)"
                  />
                </v-flex>
              </v-layout>
              <template v-if="progressBar">
                <v-progress-linear
                  indeterminate
                  color="primary"
                />
              </template>
            </v-card>
          </template>
          <template v-else>
            <img
              src="@/assets/logo_placeholder.png"
              height="200px"
            >
            <template v-if="progressBar">
              <v-progress-linear
                indeterminate
                color="primary"
              />
            </template>
          </template>
          <p class="subheading px-4 pt-3">
            Please upload a transparent PNG cropped to the edges. Image width and height should be less than 1000px.
          </p>
        </v-layout>
        <div class="px-3">
          <v-divider />
        </div>
        <v-toolbar
          color="white"
          flat
        >
          <v-toolbar-title class="blue-grey--text text--darken-3">
            Theme Color
          </v-toolbar-title>
        </v-toolbar>
        <v-layout
          align-center
          class="pa-3"
        >
          <div class="form__field pr-4">
            <div class="form__input">
              <swatches
                v-model="selectedColor"
                :colors="colorSwatch"
                :swatch-style="{ borderRadius: '0px' , padding: '0px', margin: '0px'}"
                :show-border="false"
                row-length="6"
                swatch-size="30"
                @input="changeColor"
              >
                <v-btn
                  slot="trigger"
                  :style="{color: selectedColor}"
                  flat
                  fab
                  large
                >
                  <v-icon
                    style="font-size: 70px"
                  >
                    format_color_fill
                  </v-icon>
                </v-btn>
              </swatches>
            </div>
          </div>
          <p class="subheading px-4 pt-4">
            This color will be used for styling buttons, backgrounds and email templates. <br>
            Theme color is automatically picked when you upload the logo.
          </p>
        </v-layout>
      </v-card>
      <v-snackbar
        v-model="snackbar"
        :color="snackbarColor"
        :timeout="0"
        bottom
      >
        {{ snackbarMessage }}
        <v-btn
          flat
          @click.native="snackbar = false"
        >
          Close
        </v-btn>
      </v-snackbar>
    </v-flex>
  </v-layout>
</template>
<script>
import * as Vibrant from 'node-vibrant';
import { Swatches } from 'vue-swatches';
import _get from 'lodash/get';
import rgbHex from 'rgb-hex';
import { database, storage } from '@/firebase';
import 'vue-swatches/dist/vue-swatches.min.css';

export default {
  name: 'Branding',
  components: {
    Swatches
  },
  data() {
    return {
      account: {},
      selectedColor: '',
      colorSwatch: [],

      progressBar: false,
      firebaseListeners: [],

      snackbarColor: 'primary',
      snackbar: false,
      snackbarMessage: ''
    };
  },
  computed: {
    locationId() {
      return _get(this.$store.state.VMS, 'lid');
    },
    accountRef() {
      const lid = _get(this.$store.state.VMS, 'lid');
      // return null if the location ID is not yet set
      if (!lid) return null;

      return database.ref(`vms/${lid}/settings/account`);
    }
  },
  watch: {
    accountRef: {
      handler(val) {
        if (!val) return;

        this.firebaseListeners.forEach(listenerFunc => {
          val.off('value', listenerFunc);
        });

        this.fetchAccount();
      },
      immediate: true
    },
    selectedColor: {
      handler(val) {
        this.accountRef
          .child('theme_color')
          .set(val.replace('#', ''))
          .catch(error => {
            this.snackbarMessage = error;
            this.snackbarColor = 'error';
            this.snackbar = true;
          });
      }
    }
  },
  beforeDestroy() {
    this.firebaseListeners.forEach(listenerFunc => {
      this.accountRef.off('value', listenerFunc);
    });
  },

  methods: {
    fetchAccount() {
      const accountQuery = this.accountRef.on('value', accountSnapshot => {
        if (accountSnapshot) {
          const account = accountSnapshot.val();
          this.account = account;
          const color = account.theme_color;
          this.selectedColor = this.addHash(color);
          this.progressBar = false;
        }
      });
      this.firebaseListeners.push(accountQuery);
    },

    addHash(color) {
      return `#${color}`;
    },

    changeColor(hexColor) {
      const color = hexColor.replace('#', '');
      this.account.theme_color = color;
    },

    getSwatch(url) {
      const vibrant = new Vibrant(url);
      vibrant.getPalette((err, palette) => {
        if (palette.Vibrant) {
          const hexColor = palette.Vibrant.getHex();
          this.account.theme_color = hexColor;
          this.selectedColor = hexColor;
        } else {
          Object.keys(palette).forEach(key => {
            const value = palette[key];
            if (value) {
              const hexColor = value.getHex();
              this.account.theme_color = hexColor;
              this.selectedColor = hexColor;
            }
          });
        }
        const colorPalette = Object.values(palette);
        this.colorSwatch = [];
        colorPalette.forEach(swatch => {
          if (!swatch) return;
          const { b } = swatch;
          const { g } = swatch;
          const { r } = swatch;
          const hex = this.addHash(rgbHex(r, g, b));
          this.colorSwatch.push(hex);
        });
      });
    },

    changeLogo() {
      const input = this.$refs.logoInput;
      if (input.files && input.files[0]) {
        this.progressBar = true;
        const file = input.files[0];

        const img = document.createElement('img');
        img.addEventListener('load', () => {
          if (img.width > 1024 || img.height > 1024) {
            this.snackbarColor = 'warning';
            this.snackbarMessage =
              'Image width and height should be less than 1000px';
            this.snackbar = true;
            this.progressBar = false;
          } else {
            this.account.logo = {};
            this.getSwatch(img.src);
            const storageRef = storage.ref(
              `${this.$store.state.profile.cid}/${
                this.locationId
              }/photos/logo/logo.png`
            );
            storageRef
              .put(file)
              .then(snapshot => {
                if (snapshot) {
                  snapshot.ref.getDownloadURL().then(downloadURL => {
                    if (downloadURL) {
                      this.account.logo.download_url = downloadURL;
                      this.accountRef.set(this.account).catch(error => {
                        this.snackbarMessage = error;
                        this.snackbarColor = 'error';
                        this.snackbar = true;
                      });
                      this.progressBar = false;
                      this.snackbarMessage = 'Logo has been uploaded';
                      this.snackbar = true;
                    }
                  });
                }
              })
              .catch(() => {
                this.progressBar = false;
                this.snackbarColor = 'error';
                this.snackbarMessage = 'Unable to uplaod logo!';
                this.snackbar = true;
              });
          }
          input.value = null;
        });
        img.setAttribute('src', URL.createObjectURL(file));
      }
    }
  }
};
</script>

<style>
#logoInput {
  display: none;
}
.slider {
  background-color: #fff;
  position: absolute;
  border: 1px solid #eeee;
  display: none;
}
</style>

